/**
 * A casa da Ruth no navegador. As nove telas da Etapa 6 estão aqui.
 *
 * Duas decisões de estrutura:
 *
 * **O onboarding é porta, não tela.** Enquanto a Ruth não conhece a casa não
 * existe "Hoje" para mostrar, então a primeira conversa toma a tela inteira,
 * sem barra de navegação — voltar para um app vazio não ajudaria ninguém.
 *
 * **Cada tela traz a própria cabeça** — a saudação é do dia, "Semana" é da
 * semana. A moldura em volta (menu, coluna central, barra de baixo) é sempre a
 * mesma, e mora no `Moldura` daqui de baixo.
 *
 * **Quem sabe que dia está aberto é daqui.** A tela Hoje agora anda pelos dias
 * ("posso querer ver o histórico, a agenda nos dias anteriores"), e o dia é
 * pedido nesta casa: é este arquivo que chama `api.hoje`, que desenha a
 * cabeça e a frase da manhã, e que guarda o dia de hoje de verdade para a
 * tela poder saber que o que ela está vendo não é ele.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'

import {
  api,
  deslocar,
  dataPorExtenso,
  fraseDoErro,
  guardarSessao,
  saudacao,
  sessaoAtual,
  usuariaAtual,
  type Dia,
} from './api'
import { Icone } from './componentes/Icone'
import { BarraInferior, MenuLateral, type Tela } from './componentes/Navegacao'
import { estado as estadoDosAvisos } from './push'
import './estilo.css'
import { Admin } from './telas/Admin'
import { Casa } from './telas/Casa'
import { Compras } from './telas/Compras'
import { Compromissos } from './telas/Compromissos'
import { Entrar } from './telas/Entrar'
import { Configuracoes } from './telas/Configuracoes'
import { Hoje } from './telas/Hoje'
import { Mais } from './telas/Mais'
import { Onboarding } from './telas/Onboarding'
import { Remedios } from './telas/Remedios'
import { Ruth } from './telas/Ruth'
import { Semana } from './telas/Semana'

/**
 * O painel de operação vive em `/admin`, fora do app da mãe.
 *
 * Mesma origem e mesmo build, sessões diferentes: quem separa de verdade é o
 * servidor, que exige escopo de administrador em toda rota de `/api/admin`.
 * Aqui o caminho só escolhe qual das duas coisas montar.
 */
function ehPainelDeOperacao(): boolean {
  return window.location.pathname.startsWith('/admin')
}

export function App() {
  if (ehPainelDeOperacao()) {
    return <Admin />
  }

  return <AppDaMae />
}

function AppDaMae() {
  const [tela, setTela] = useState<Tela>('hoje')
  // Quem já entrou tem sessão. O `?usuaria=` continua valendo fora de produção
  // porque é o que faz a casa demo abrir sem servidor de e-mail.
  const [entrou, setEntrou] = useState(() => Boolean(sessaoAtual() || usuariaAtual()))
  const [dia, setDia] = useState<Dia | null>(null)
  /**
   * Qual dia está aberto. `null` é hoje.
   *
   * "Posso querer ver o histórico, a agenda nos dias anteriores." A Semana já
   * andava no tempo e esta tela era só hoje — não existia lugar nenhum no app
   * que mostrasse um dia inteiro que já passou, com as horas, os blocos e os
   * compromissos dele.
   */
  const [qual, setQual] = useState<string | null>(null)
  /**
   * O dia de hoje na casa, dito pelo servidor.
   *
   * Guardado porque só a resposta sem `dia` pedido responde isso: quando ela
   * pede o sábado, o `hoje` da resposta volta sendo o sábado — o campo diz
   * qual dia foi desenhado, não qual dia é. E é o servidor quem sabe, no fuso
   * da casa; perguntar ao relógio do aparelho é a mãe em viagem vendo a
   * segunda-feira marcada como domingo.
   */
  const [hojeNaCasa, setHojeNaCasa] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  // null enquanto não sabemos: mostrar Hoje antes da resposta faria a tela
  // piscar um dia vazio na cara de quem nem se apresentou ainda.
  const [conhecida, setConhecida] = useState<boolean | null>(null)

  const carregar = useCallback(async () => {
    try {
      const resposta = await api.hoje(qual ?? undefined)
      setDia(resposta)
      if (qual === null) setHojeNaCasa(resposta.hoje)
      setErro(null)
    } catch (e) {
      // A frase do servidor passa inteira: "Esse dia está longe demais. Eu
      // guardo dois meses para trás." é escrita para ela ler. O `HTTP 502` do
      // proxy e o `Failed to fetch` do elevador sem sinal viram a frase de
      // sempre — antes disto eles apareciam crus no lugar do dia dela.
      setErro(fraseDoErro(e, 'não consegui carregar seu dia'))
    }
  }, [qual])

  useEffect(() => {
    if (!entrou) return
    api
      .onboardingEstado()
      .then((passo) => setConhecida(passo.concluido))
      // Sem resposta, seguimos para o app: a mãe que já usa a Ruth não pode
      // ficar presa numa apresentação por causa de uma requisição que falhou.
      .catch(() => setConhecida(true))
  }, [entrou])

  useEffect(() => {
    if (conhecida) void carregar()
  }, [conhecida, carregar])

  const marcarFeito = useCallback(
    async (ocorrencia: string) => {
      await api.marcarFeito(ocorrencia)
      await carregar()
    },
    [carregar],
  )

  const desmarcar = useCallback(
    async (ocorrencia: string) => {
      await api.desmarcarFeito(ocorrencia)
      await carregar()
    },
    [carregar],
  )

  const adiar = useCallback(
    async (ocorrencia: string) => {
      await api.maisTarde(ocorrencia)
      await carregar()
    },
    [carregar],
  )

  const acrescentarCompra = useCallback(async (nome: string) => {
    await api.acrescentarCompra(nome)
  }, [])

  /**
   * Trocar de tela, e voltar para hoje ao entrar em Hoje.
   *
   * A aba tem o nome do dia: quem toca em "Hoje" está pedindo hoje. Sem isto,
   * ela olharia um sábado de agosto, iria ver a Semana, voltaria em "Hoje" e
   * cairia de novo no sábado de agosto — sem ter pedido, e sem saber por quê.
   */
  const trocarDeTela = useCallback((nova: Tela) => {
    if (nova === 'hoje') setQual(null)
    setTela(nova)
  }, [])

  if (!entrou) {
    return (
      <Entrar
        aoEntrar={(sessao) => {
          guardarSessao(sessao)
          setEntrou(true)
        }}
      />
    )
  }

  if (conhecida === null) {
    return <div className="app" />
  }

  if (!conhecida) {
    return <Onboarding aoConcluir={() => setConhecida(true)} />
  }

  // A conversa tem altura fixa: o compositor não pode rolar junto com as falas.
  if (tela === 'ruth') {
    return (
      <Moldura tela={tela} aoTrocar={trocarDeTela} className="conteudo--conversa">
        {/* O `hoje` da conversa é o dia de verdade, e não o que ela está vendo
            na tela Hoje: "marca pra amanhã" dito hoje é amanhã, mesmo que ela
            tenha acabado de olhar o sábado que passou. */}
        <Ruth hoje={hojeNaCasa} aoMudarACasa={() => void carregar()} />
      </Moldura>
    )
  }

  const dentro: Partial<Record<Tela, ReactNode>> = {
    semana: <Semana aoAjustarODia={() => trocarDeTela('config')} />,
    casa: (
      <Casa aoMudarOPlano={() => void carregar()} aoFalarComARuth={() => trocarDeTela('ruth')} />
    ),
    mais: <Mais aoAbrir={trocarDeTela} />,
    remedios: <Remedios aoFotografarReceita={() => trocarDeTela('ruth')} />,
    compras: <Compras aoAcrescentar={acrescentarCompra} />,
    compromissos: <Compromissos aoFalarComARuth={() => trocarDeTela('ruth')} />,
    config: <Configuracoes />,
  }

  if (dentro[tela]) {
    return (
      <Moldura tela={tela} aoTrocar={trocarDeTela}>
        {dentro[tela]}
      </Moldura>
    )
  }

  // Que dia ela está vendo, comparado com o de hoje. Texto ISO contra texto
  // ISO: nenhuma das duas datas passa pelo relógio do aparelho.
  const passou = Boolean(dia && hojeNaCasa && dia.hoje < hojeNaCasa)
  const futuro = Boolean(dia && hojeNaCasa && dia.hoje > hojeNaCasa)
  const emHoje = !passou && !futuro

  return (
    <Moldura tela={tela} aoTrocar={trocarDeTela}>
      <header className="cabeca">
        <div className="cabeca__datas">
          <span className="cabeca__data">{dia ? dataPorExtenso(dia.hoje) : ' '}</span>
          <h1 className="cabeca__saudacao">{tituloDoDia(dia, passou, futuro)}</h1>
        </div>
        <Sino aoAbrir={() => trocarDeTela('config')} />
      </header>

      {/* Andar pelos dias, com a mesma forma e as mesmas palavras da Semana:
          as duas setas nas pontas e, no meio, o caminho de volta. Fica embaixo
          do título porque é ali que a mãe está olhando quando pensa "e ontem?".

          O meio faz duas coisas de uma vez. Em hoje ele é uma palavra apagada
          que só diz onde ela está; fora de hoje vira botão da cor do toque —
          o segundo sinal de que isto não é hoje, e a volta em um toque, sem
          ela precisar contar quantos dias andou para trás.

          O passo sai de `dia.hoje`, o dia que o servidor desenhou, e não do
          que ela pediu: se um pedido não der certo, a seta continua andando a
          partir do que está na tela em vez de escorregar sozinha. */}
      {dia && (
        <div className="semana__navegar">
          <button
            type="button"
            className="semana__passo"
            aria-label="Dia anterior"
            onClick={() => setQual(deslocar(dia.hoje, -1))}
          >
            <Icone nome="voltar" tamanho={18} />
          </button>

          {emHoje ? (
            <span className="semana__hoje semana__hoje--parada">hoje</span>
          ) : (
            <button type="button" className="semana__hoje" onClick={() => setQual(null)}>
              Voltar pra hoje
            </button>
          )}

          <button
            type="button"
            className="semana__passo semana__passo--frente"
            aria-label="Próximo dia"
            onClick={() => setQual(deslocar(dia.hoje, 1))}
          >
            <Icone nome="voltar" tamanho={18} />
          </button>
        </div>
      )}

      {/* A frase fica em qualquer dia: ela é do dia, não do agora — é o que a
          Ruth disse naquela manhã, e é a coisa mais parecida com uma lembrança
          que a tela tem. Em dia que ainda vem ela simplesmente não existe. */}
      {dia?.frase_do_dia && <p className="frase-do-dia">{dia.frase_do_dia}</p>}

      <div className="corpo">
        {erro && <p className="vazio">{erro}</p>}

        {dia && (
          <Hoje
            // Trocar de dia recomeça a tela: um conserto de bloco aberto no
            // sábado não pode continuar aberto quando ela chega na segunda. O
            // bloco repete todo dia com o mesmo id, então sem isto o painel
            // ficaria lá, dizendo respeito a outro dia.
            key={dia.hoje}
            dia={dia}
            hojeNaCasa={hojeNaCasa}
            aoMarcarFeito={marcarFeito}
            aoDesmarcar={desmarcar}
            aoAdiar={adiar}
            aoAbrirCompras={() => trocarDeTela('compras')}
            aoMudarACasa={() => void carregar()}
          />
        )}
      </div>
    </Moldura>
  )
}

/**
 * O que o título da tela diz — e é ele que conta à mãe onde ela está.
 *
 * A saudação é do agora: "Bom dia, Dani" em cima de um sábado que já acabou é
 * a tela dizendo que aquele dia está começando. Fora de hoje, esse lugar passa
 * a responder "que dia é este que estou vendo?" — a data por extenso continua
 * na linha de cima, dizendo qual.
 */
function tituloDoDia(dia: Dia | null, passou: boolean, futuro: boolean): string {
  if (!dia) return 'Ruth'
  if (passou) return 'Esse dia já passou'
  if (futuro) return 'Esse dia ainda vem'
  return `${saudacao(dia.agora)}, ${dia.nome_tratamento}`
}


/**
 * O sino, que até agora não fazia nada.
 *
 * Era um botão sem `onClick` no topo de toda tela, exatamente onde todo
 * aplicativo do mundo põe as notificações — a mãe toca e nada acontece, nem uma
 * frase. Ou liga, ou sai.
 *
 * Ligou no lugar mais útil: a Ruth só serve se ela chega. E hoje o interruptor
 * dos avisos está enterrado em Mais → Ajustar a Ruth → rolar até o fim, então
 * quem nunca achou nunca deu a permissão, e o produto inteiro ficou mudo sem
 * ninguém perceber. O ponto no sino é o único aviso de que isso aconteceu.
 *
 * Não é caixa de mensagens, e não vai virar: a conversa é onde a Ruth fala, e
 * silêncio é funcionalidade (regra 4). Uma caixinha de avisos não lidos seria
 * mais uma coisa cobrando a mãe.
 */
function Sino({ aoAbrir }: { aoAbrir: () => void }) {
  const [mudo, setMudo] = useState(false)

  useEffect(() => {
    let vivo = true
    void estadoDosAvisos()
      .then((avisos) => {
        // Sem suporte no navegador não há o que ligar, e um ponto vermelho que
        // ela não pode resolver é só uma cobrança.
        if (vivo) setMudo(avisos.suportado && !avisos.ligado)
      })
      .catch(() => {})
    return () => {
      vivo = false
    }
  }, [])

  return (
    <button
      type="button"
      className="cabeca__sino"
      aria-label={mudo ? 'Avisos desligados' : 'Avisos'}
      onClick={aoAbrir}
    >
      <Icone nome="sino" />
      {mudo && <span className="cabeca__ponto" aria-hidden="true" />}
    </button>
  )
}

function Moldura({
  tela,
  aoTrocar,
  className,
  children,
}: {
  tela: Tela
  aoTrocar: (tela: Tela) => void
  className?: string
  children: ReactNode
}) {
  return (
    <div className="app">
      <MenuLateral atual={tela} aoTrocar={aoTrocar} />
      <main className={className ? `conteudo ${className}` : 'conteudo'}>{children}</main>
      <BarraInferior atual={tela} aoTrocar={aoTrocar} />
    </div>
  )
}
